import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { buildersApi, ApiError } from "@/api";
import type { BuilderDetailResponse, BuilderPerson, JobBuilderRole } from "@/types";
import { Button, Spinner, Pill, Toast, useToast } from "@/components/ui";
import { formatDateShort } from "@/lib/format";
import { JobStagePill } from "../jobs/JobChips";
import { BuilderFormDrawer } from "./BuilderFormDrawer";
import { InvitePersonDrawer } from "./InvitePersonDrawer";
import "../clients/clients.css";
import "./builders.css";

export function jobBuilderRoleLabel(role: JobBuilderRole | string): string {
  switch (role) {
    case "tendering":
      return "Tendering";
    case "referred":
      return "Referred us";
    default:
      return "Appointed";
  }
}

/** "Chat from 3 Sep 2026" or "Full chat history" — the firm's visibility
 *  cutoff on a job (D5). */
export function chatVisibilityLabel(chatFrom: number): string {
  return chatFrom > 0 ? `Chat from ${formatDateShort(chatFrom)}` : "Full chat history";
}

function personState(p: BuilderPerson): "active" | "pending" | "disabled" {
  if (!p.is_active) return "disabled";
  return p.has_signed_in ? "active" : "pending";
}

/** The builder hub — GET /builders/detail drives the firm card (click to edit),
 *  the people with app logins (invite / resend / deactivate) and the jobs the
 *  firm is or was attached to. Archive is refused while any job link is live. */
export function BuilderDetail() {
  const { id } = useParams<{ id: string }>();
  const builderId = Number(id);

  const [detail, setDetail] = useState<BuilderDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [editing, setEditing] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [personBusyId, setPersonBusyId] = useState<number | null>(null);
  const { toast, showToast } = useToast();

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    buildersApi
      .getBuilderDetail(builderId)
      .then((res) => {
        if (!cancelled) setDetail(res);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Could not load the builder.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [builderId]);

  useEffect(() => load(), [load]);

  if (loading) {
    return (
      <section className="client-detail">
        <Link to="/staff/builders" className="open-apps__back">← Builders</Link>
        <div className="client-detail__loading"><Spinner label="Loading builder…" /></div>
      </section>
    );
  }

  if (!detail) {
    return (
      <section className="client-detail">
        <Link to="/staff/builders" className="open-apps__back">← Builders</Link>
        <p className="leads__error" role="alert">{error || "Builder not found."}</p>
      </section>
    );
  }

  const { builder, jobs, people } = detail;
  const isArchived = builder.status === "archived";
  const liveJobs = jobs.filter((j) => j.is_active);
  const canArchive = liveJobs.length === 0;
  const contactName = `${builder.contact_first_name} ${builder.contact_last_name}`.trim();

  async function archive() {
    if (busy || isArchived || !canArchive) return;
    if (!window.confirm(`Archive ${builder.company_name}? You'll still find it via "Include archived" on the list.`)) return;
    setBusy(true);
    setError(undefined);
    try {
      await buildersApi.archiveBuilder(builderId);
      showToast("Builder archived.");
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not archive the builder.");
    } finally {
      setBusy(false);
    }
  }

  async function reopen() {
    if (busy || !isArchived) return;
    setBusy(true);
    setError(undefined);
    try {
      await buildersApi.reopenBuilder(builderId);
      showToast("Builder reopened.");
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not reopen the builder.");
    } finally {
      setBusy(false);
    }
  }

  async function resend(p: BuilderPerson) {
    if (personBusyId != null) return;
    const verb = p.is_active ? "Resend the invite to" : "Restore access and resend the invite to";
    if (!window.confirm(`${verb} ${p.email}? They'll get an email with the app download links.`)) return;
    setPersonBusyId(p.portal_user_id);
    setError(undefined);
    try {
      const res = await buildersApi.invitePerson(builderId, { name: p.name, email: p.email, send_invite_email: true });
      if (res.email_sent) showToast(`Invite resent to ${res.email}.`);
      else showToast(`${res.email} has access, but no email was sent — the invite template isn't set up in SendGrid yet.`);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not resend the invite.");
    } finally {
      setPersonBusyId(null);
    }
  }

  async function deactivate(p: BuilderPerson) {
    if (personBusyId != null) return;
    if (!window.confirm(`Switch off app access for ${p.name || p.email}? They lose the chat immediately; re-inviting the same email restores it.`)) return;
    setPersonBusyId(p.portal_user_id);
    setError(undefined);
    try {
      await buildersApi.deactivatePerson(builderId, p.portal_user_id);
      showToast("Access switched off.");
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not deactivate this login.");
    } finally {
      setPersonBusyId(null);
    }
  }

  return (
    <section className="client-detail">
      <Link to="/staff/builders" className="open-apps__back">← Builders</Link>

      <header className="client-detail__head">
        <div>
          <h1>{builder.company_name}</h1>
          <p className="client-detail__tags">
            <span className="clients-badge clients-badge--company">Builder</span>
            {builder.trade ? <span className="clients-badge clients-badge--muted">{builder.trade}</span> : null}
            {isArchived ? <span className="clients-badge clients-badge--muted">Archived</span> : null}
          </p>
        </div>
        <div className="client-detail__actions">
          {!isArchived ? (
            <Button variant="primary" onClick={() => setInviting(true)} disabled={busy}>Invite person to app</Button>
          ) : null}
          {isArchived ? (
            <Button variant="secondary" onClick={reopen} loading={busy} disabled={busy}>Reopen builder</Button>
          ) : (
            <Button
              variant="secondary"
              onClick={archive}
              loading={busy}
              disabled={busy || !canArchive}
              title={canArchive ? "Archive this builder." : `Can't archive yet — still attached to ${liveJobs.length} job${liveJobs.length === 1 ? "" : "s"}.`}
            >
              Archive builder
            </Button>
          )}
        </div>
      </header>

      {error ? <p className="leads__error" role="alert" onClick={() => setError(undefined)}>{error}</p> : null}

      <div className="client-detail__grid">
        <section
          className="client-card client-card--editable"
          role="button"
          tabIndex={0}
          aria-label="Edit builder"
          onClick={() => setEditing(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setEditing(true);
            }
          }}
        >
          <div className="client-card__head">
            <h2>Firm</h2>
            <span className="client-card__edit" aria-hidden="true">Edit</span>
          </div>
          <dl className="client-kv">
            <KV label="Contact">{contactName ? `${contactName}${builder.contact_role ? ` · ${builder.contact_role}` : ""}` : "—"}</KV>
            <KV label="Email">{builder.email ? <a href={`mailto:${builder.email}`} onClick={(e) => e.stopPropagation()}>{builder.email}</a> : "—"}</KV>
            <KV label="Phone">{builder.phone ? <a href={`tel:${builder.phone}`} onClick={(e) => e.stopPropagation()}>{builder.phone}</a> : "—"}</KV>
            <KV label="Trade">{builder.trade || "—"}</KV>
            <KV label="Website">{builder.website ? <a href={builder.website.startsWith("http") ? builder.website : `https://${builder.website}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>{builder.website}</a> : "—"}</KV>
          </dl>
          {builder.notes ? (
            <div className="client-card__notes">
              <h3>Notes</h3>
              <p>{builder.notes}</p>
            </div>
          ) : null}
        </section>

        <section className="client-card">
          <div className="client-card__head">
            <h2>App logins <span className="clients-count">{people.length}</span></h2>
          </div>
          {people.length === 0 ? (
            <p className="client-card__empty">Nobody at this firm has app access yet. Invite a person to bring them into the chat on the jobs this firm is attached to.</p>
          ) : (
            <ul className="builder-people">
              {people.map((p) => {
                const state = personState(p);
                const pbusy = personBusyId === p.portal_user_id;
                return (
                  <li key={p.portal_user_id} className="builder-person">
                    <div className="builder-person__main">
                      <span className="builder-person__name">
                        {p.name || p.email}{" "}
                        {state === "active" ? <span className="clients-badge clients-badge--ok">Active</span> : null}
                        {state === "pending" ? <span className="clients-badge clients-badge--warn">Invited</span> : null}
                        {state === "disabled" ? <span className="clients-badge clients-badge--muted">Access off</span> : null}
                      </span>
                      <span className="builder-person__meta">
                        {p.email}
                        {p.invited_at > 0 ? ` · invited ${formatDateShort(p.invited_at)}` : ""}
                        {p.last_login_at > 0 ? ` · last signed in ${formatDateShort(p.last_login_at)}` : ""}
                      </span>
                    </div>
                    <div className="builder-person__actions">
                      <Button variant="ghost" size="sm" loading={pbusy} disabled={personBusyId != null} onClick={() => resend(p)}>
                        {state === "disabled" ? "Restore" : "Resend"}
                      </Button>
                      {state !== "disabled" ? (
                        <Button variant="ghost" size="sm" disabled={personBusyId != null} onClick={() => deactivate(p)}>Switch off</Button>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          <p className="client-card__hint">
            A login sees the chat on every job the firm is attached to, and nothing else. One login per email address.
          </p>
        </section>
      </div>

      <section className="client-card">
        <h2>Jobs <span className="clients-count">{jobs.length}</span></h2>
        {jobs.length === 0 ? (
          <p className="client-card__empty">Not attached to any job yet. Add the builder from a job's Clients tab.</p>
        ) : (
          <ul className="client-jobs">
            {jobs.map((j) => (
              <li key={j.job_builder_id}>
                <Link to={`/staff/jobs/${j.job_id}`} className="client-jobs__row">
                  <span className="client-jobs__num numeric">{j.job_number || "—"}</span>
                  <span className="client-jobs__name">
                    {j.job_name || j.address || "Untitled job"}
                    <span className="job-builder-card__line">
                      {j.is_active ? chatVisibilityLabel(j.chat_from) : `Removed ${j.removed_at > 0 ? formatDateShort(j.removed_at) : ""}`.trim()}
                    </span>
                  </span>
                  <span className="client-jobs__meta">
                    {j.is_active ? <Pill tone="info">{jobBuilderRoleLabel(j.role)}</Pill> : <Pill tone="neutral">Removed</Pill>}
                    {j.status ? <JobStagePill stage={j.status} /> : <span className="client-jobs__status">—</span>}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      {editing ? (
        <BuilderFormDrawer
          builder={builder}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            showToast("Builder updated.");
            load();
          }}
        />
      ) : null}

      {inviting ? (
        <InvitePersonDrawer
          builderId={builderId}
          builderName={builder.company_name}
          defaultName={people.length === 0 ? contactName : ""}
          defaultEmail={people.length === 0 ? builder.email : ""}
          onClose={() => setInviting(false)}
          onInvited={(res) => {
            setInviting(false);
            if (res.email_sent) showToast(res.created ? `Invited — app links sent to ${res.email}.` : `Invite resent to ${res.email}.`);
            else if (res.created) showToast(`Login created for ${res.email}${res.email_reason === "not_requested" ? " (no email sent)." : ", but no email was sent — the invite template isn't set up in SendGrid yet."}`);
            else showToast(`${res.email} already has access.`);
            load();
          }}
        />
      ) : null}

      <Toast toast={toast} />
    </section>
  );
}

function KV({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="client-kv__row">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}
